import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { packages, PRICE_TIER_SM, PRICE_TIER_SUV, DetailPackage } from '../data/packages';
import { PackageIconGlyph } from './PackageIconGlyph';

/** Full pricing grid for every detailing package. */
export function Services() {
  return <section className="w-full bg-black py-16 px-4" id="packages" aria-labelledby="packages-heading" data-id="element-500" data-mp="18xag1k8ocwlc">
      <div className="max-w-7xl mx-auto" data-id="element-501" data-mp="z54sqntdb3ch">
        <div className="text-center mb-12" data-id="element-502" data-mp="z43fccovq4rl">
          <h2 id="packages-heading" className="text-3xl sm:text-5xl md:text-6xl font-black uppercase mb-4" data-id="element-503" data-mp="z3bjd0rde50s">
            <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500" data-id="element-504" data-mp="q4gdxqw5gyyj">
              Mobile Car Detailing Packages & Pricing
            </span>
          </h2>
          <div className="h-1 w-24 bg-red-600 mx-auto rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-505" data-mp="g6k555qoqg3l" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mt-4" data-id="element-506" data-mp="36by0qnf3qnjj">
            Transparent pricing for professional mobile car detailing in Ventura
            County. Every service includes premium eco-friendly products, expert
            care, and the convenience of at-your-door service throughout
            Camarillo, Thousand Oaks, and beyond.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-8" data-id="element-507" data-mp="3q0gt1dvemh2g">
          {packages.map((pkg, index) => <ServiceCard key={pkg.slug} pkg={pkg} index={index} data-id="element-508" data-mp="3u1sbmzfv2215" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  pkg,
  index
}: {
  pkg: DetailPackage;
  index: number;
}) {
  const isBlue = pkg.accent === 'blue';
  const borderColor = isBlue ? 'border-blue-500' : 'border-green-500';
  const shadowColor = isBlue ? 'shadow-blue-500/20' : 'shadow-green-500/20';
  const titleColor = isBlue ? 'text-blue-400' : 'text-green-400';
  const gradient = isBlue ? 'from-blue-900/20 to-transparent' : 'from-green-900/20 to-transparent';
  return <motion.article initial={{
    y: 40,
    opacity: 0
  }} whileInView={{
    y: 0,
    opacity: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.06,
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className={`relative flex flex-col bg-zinc-900/80 border-2 ${borderColor} rounded-lg overflow-hidden shadow-[0_0_30px_-5px_rgba(0,0,0,0.3)] ${shadowColor}`} data-id="element-509" data-mp="1pjb01u24oe1h">
      <div className={`p-6 bg-gradient-to-b ${gradient} border-b border-zinc-800`} data-id="element-510" data-mp="16v625385jnal">
        <div className="flex items-center justify-between gap-3 mb-2" data-id="element-511" data-mp="11tgr3nkxj9mi">
          <h3 className={`text-2xl sm:text-3xl font-black uppercase tracking-wide whitespace-nowrap ${titleColor}`} data-id="element-512" data-mp="xsem0q4c03cm">
            {pkg.name}
          </h3>
          <PackageIconGlyph icon={pkg.icon} className={`w-6 h-6 shrink-0 ${titleColor}`} data-id="element-513" data-mp="svo6c3wfv9rq" />
        </div>
        <p className="mt-1 text-lg font-black text-white" data-id="element-514" data-mp="2s4v0ufb70pt3">
          {pkg.requirement ?? pkg.summary}
        </p>
      </div>

      <div className="p-6 flex-1" data-id="element-515" data-mp="2urdnj2de2unr">
        <ul className="space-y-4" data-id="element-516" data-mp="2uqcaisctby3e">
          {pkg.features.map(feature => <li key={feature} className="flex items-start text-gray-300" data-id="element-517" data-mp="2zq6yan1ak7k8">
              <Check className="w-5 h-5 text-white mr-3 shrink-0" aria-hidden="true" data-id="element-518" data-mp="2zodtlda8nxev" />
              <span className="font-medium text-lg" data-id="element-519" data-mp="34hypmzs9uhbf">{feature}</span>
            </li>)}
        </ul>
      </div>

      <div className="p-6 bg-zinc-950 border-t border-zinc-800" data-id="element-520" data-mp="39icnjp05savq">
        <div className="grid grid-cols-2 gap-4 text-center" data-id="element-521" data-mp="2btt5of91kwcn">
          <div className="flex flex-col" data-id="element-522" data-mp="2grcidaq3d12h">
            <span className="text-xs sm:text-sm text-gray-500 uppercase font-bold tracking-wider mb-1 whitespace-nowrap" data-id="element-523" data-mp="2gsdyvupvmfrl">
              {PRICE_TIER_SM}
            </span>
            <span className={`text-3xl sm:text-4xl font-black ${titleColor}`} data-id="element-524" data-mp="2psj3xy7ll22l">
              {pkg.priceSm}
            </span>
          </div>
          <div className="flex flex-col border-l border-zinc-800" data-id="element-525" data-mp="106trab0ld7y">
            <span className="text-xs sm:text-sm text-gray-500 uppercase font-bold tracking-wider mb-1 whitespace-nowrap" data-id="element-526" data-mp="510i24sdcu1d">
              {PRICE_TIER_SUV}
            </span>
            <span className={`text-3xl sm:text-4xl font-black ${titleColor}`} data-id="element-527" data-mp="3urfow9fgv9yq">
              {pkg.priceSuv}
            </span>
          </div>
        </div>
        <Link to={pkg.path} className="mt-6 flex items-center justify-center gap-2 w-full py-3 border border-zinc-700 text-white font-bold text-sm uppercase tracking-wider rounded-lg hover:bg-zinc-800 hover:border-zinc-500 transition-colors duration-150" data-id="element-528" data-mp="dkdk5cssnzwx">
          View Service
          <ArrowRight className="w-4 h-4" aria-hidden="true" data-id="element-529" data-mp="hjw3i44yw2xn" />
        </Link>
      </div>
    </motion.article>;
}